import { ApiError, path } from './client'
import type { ApiClient, Query } from './client'
import type { Page } from '@namu/protocol'
import type { CasePriority, FieldTask, Instant, TaskReport, TaskStatus, TaskVerification } from './types'

/**
 * Work for a named member of staff, and what came back from it.
 *
 * Agents file reports by voice through the same app the community uses, which
 * is the point of the whole arrangement: the person doing the work should not
 * have to become a typist to record it.
 *
 * What is deliberately not here is any endpoint for a worker's position. A
 * report can carry the place it was filed from, because that is a fact about
 * the work. A track through the day is not, and the literature on worker
 * surveillance in this sector is clear that it corrodes exactly the trust the
 * work depends on.
 *
 * Verification runs the other way round from the rest of the sector.
 * CommCare Connect and its peers verify the worker with biometrics, GPS and
 * photographs, and it does work: adversarial testing caught 100% of fabricated
 * submissions. But it is a surveillance regime aimed at the lowest-paid person
 * in the chain. Namu holds something that stack does not: the beneficiary's own
 * voice. Asking the people served is a better signal, because no GPS ping can
 * fake somebody saying nobody came, and it closes the loop with the community
 * instead of only auditing the worker.
 *
 * ## Three things here were calling routes that have never existed
 *
 * Found by running the console against the real server rather than against a
 * stubbed `fetch`, which is the only way any of them could have been found:
 * both test suites were green throughout.
 *
 * `reports(id)` fetched `/tasks/:id/reports`. There is no such path, and there
 * does not need to be: `GET /tasks/:id` already answers `{ task, reports }`, so
 * the record and what was filed against it arrive together. `get` now returns
 * both and the second request is gone.
 *
 * `setStatus(id, status)` posted a status in a body to `/tasks/:id/status`. The
 * server has `/accept`, `/start`, `/cancel` and `/assign` instead, and its shape
 * is the better one: a machine with named transitions cannot be driven into a
 * state nobody wrote a transition for, whereas a status in a body is a
 * free-text field that has to be defended against on every request. Only
 * `cancel` is here, because cancelling is the one move this console makes.
 * Accepting and starting belong to the agent on their own phone, and approval
 * and rejection travel with the review note through `review`.
 *
 * `verifications(id)` named a route that did not exist at all. It does now.
 *
 * ## Every response is unwrapped here
 *
 * The server answers a single record as `{ task }` or `{ task, report }`, and a
 * list as `{ items, nextCursor }`. This module is the only place that knows
 * that. What it replaced was worse than a wrong path: `get` was typed as
 * returning a `FieldTask`, actually returned `{ task: … }`, and every field the
 * screen read came back undefined with nothing anywhere reporting a fault.
 */

/**
 * Reads one record out of the envelope the server sends it in.
 *
 * A missing envelope is raised as a failure rather than passed on as
 * `undefined`. Handing `undefined` to a screen is the worse of the two: the
 * dialog that assigns a task would navigate to `/tasks/undefined`, and the
 * record screen would draw a heading with no text in it. Both read as the
 * console being broken with no clue as to where. An `ApiError` lands in the
 * `catch` every one of these call sites already has.
 *
 * `retry` rather than `fatal`, because the realistic cause is a proxy or a
 * half-deployed server rather than a request that can never work.
 */
function unwrap<T>(body: unknown, key: string): T {
  const record = (body as Record<string, unknown> | null)?.[key]
  if (record === undefined || record === null) throw unreadable()
  return record as T
}

/**
 * The same for a list that arrives beside a record rather than as a page.
 *
 * Absent or not an array is a failure and not an empty list, and the difference
 * is the whole reason this is a function. "Nothing has been filed yet" and "the
 * answer could not be read" look identical on screen, and a supervisor who is
 * shown the first for the second concludes the agent never filed and goes and
 * asks them about it.
 */
function listOf<T>(body: unknown, key: string): readonly T[] {
  const rows = (body as Record<string, unknown> | null)?.[key]
  if (!Array.isArray(rows)) throw unreadable()
  return rows as T[]
}

/**
 * The same, for a list the record can be read without.
 *
 * Null rather than a throw, because a `{ task }` that arrived without its
 * `reports` is still a task worth putting on screen. Failing the whole request
 * would take the title, the assignee, the due date and the cancel button with
 * it, and every one of those was in the answer. The panel that draws the
 * reports says it could not read them; the rest of the record is unaffected.
 */
function optionalListOf<T>(body: unknown, key: string): readonly T[] | null {
  const rows = (body as Record<string, unknown> | null)?.[key]
  return Array.isArray(rows) ? (rows as T[]) : null
}

/** The same again for a cursor page, where `items` carries the same argument. */
function pageFrom<T>(body: unknown): Page<T> {
  const envelope = body as { items?: unknown; nextCursor?: unknown } | null
  if (!Array.isArray(envelope?.items)) throw unreadable()
  return {
    items: envelope.items as T[],
    ...(typeof envelope.nextCursor === 'string' ? { nextCursor: envelope.nextCursor } : {}),
  }
}

function unreadable(): ApiError {
  return new ApiError({
    status: 200,
    code: 'internal',
    message: 'Namu sent an answer this console could not read.',
    recovery: 'retry',
  })
}

export interface TaskListQuery extends Query {
  projectId?: string
  status?: TaskStatus
  priority?: CasePriority
  /**
   * A staff user id.
   *
   * Resolved by the caller from the signed-in principal when somebody picks
   * "assigned to me", because the server takes an id here and nothing else.
   * That is not a weakness: a role which may not browse the organization has
   * its own id forced into this filter server-side whatever it sends, so the
   * scope an agent gets does not depend on what the console puts in the query.
   */
  assignedTo?: string
  /** Past due and not finished. The list a supervisor opens first. */
  overdue?: boolean
  limit?: number
  cursor?: string
}

export interface TaskInput {
  projectId: string
  title: string
  instructions?: string | null
  assigneeId?: string | null
  dueAt?: Instant | null
  priority?: CasePriority
  locationName?: string | null
  /** Set when the task exists because of a case. */
  caseId?: string | null
}

/**
 * Approving or rejecting a filed report.
 *
 * A boolean rather than an outcome word, which is the server's spelling and the
 * narrower of the two: `approved: true` has one meaning, and a string field has
 * as many as somebody can type.
 */
export interface TaskReviewInput {
  approved: boolean
  note?: string | null
}

/** A task and everything filed against it, which arrive in one answer. */
export interface TaskRecord {
  task: FieldTask
  /** Null when the answer carried no readable list, which is not the same as none. */
  reports: readonly TaskReport[] | null
}

/** A review changes the report and moves the task, so both come back. */
export interface ReviewedReport {
  task: FieldTask
  report: TaskReport
}

export function tasksApi(http: ApiClient) {
  return {
    async list(query: TaskListQuery = {}) {
      return pageFrom<FieldTask>(await http.request('/v1/org/tasks', { query }))
    },

    /**
     * The task and its reports, in one request.
     *
     * Two requests would let the reports fail separately from the record, which
     * sounds better and is not: they come from one `select` on one endpoint, so
     * the second request could only ever fail for a reason the first would have
     * failed for too, and it would double the load on a screen that is opened
     * all day.
     */
    async get(id: string): Promise<TaskRecord> {
      const body = await http.request(path`/v1/org/tasks/${id}`)
      return {
        task: unwrap<FieldTask>(body, 'task'),
        reports: optionalListOf<TaskReport>(body, 'reports'),
      }
    },

    async create(input: TaskInput) {
      return unwrap<FieldTask>(
        await http.request('/v1/org/tasks', { method: 'POST', body: input }),
        'task',
      )
    },

    async update(id: string, input: Partial<TaskInput>) {
      return unwrap<FieldTask>(
        await http.request(path`/v1/org/tasks/${id}`, { method: 'PATCH', body: input }),
        'task',
      )
    },

    /**
     * Calls the work off.
     *
     * The only transition this console makes. There is no `setStatus`, and the
     * server has no route that would accept one: every move is its own path, so
     * a jump from `open` to `approved` has no spelling rather than being a move
     * the server has to refuse.
     */
    async cancel(id: string) {
      return unwrap<FieldTask>(
        await http.request(path`/v1/org/tasks/${id}/cancel`, { method: 'POST' }),
        'task',
      )
    },

    /**
     * Approves or rejects a submitted report.
     *
     * A rejection carries a note, always. An agent who is told their report was
     * rejected and not why has been given a task with no instructions, and the
     * second submission will be a guess.
     */
    async review(id: string, reportId: string, input: TaskReviewInput): Promise<ReviewedReport> {
      const body = await http.request(path`/v1/org/tasks/${id}/reports/${reportId}/review`, {
        method: 'POST',
        body: input,
      })
      return { task: unwrap<FieldTask>(body, 'task'), report: unwrap<TaskReport>(body, 'report') }
    },

    /**
     * What the people the work was for said about it.
     *
     * Read-only from the console, and refused outright by the server to the
     * person the task is assigned to. Who gets asked is chosen server-side and
     * at random, and no account identifier is in the payload at all: a worker
     * who knows which members will be called can arrange the answer, which is
     * the one failure mode this check actually has.
     */
    async verifications(id: string) {
      return listOf<TaskVerification>(
        await http.request(path`/v1/org/tasks/${id}/verifications`),
        'verifications',
      )
    },
  }
}

export type TasksApi = ReturnType<typeof tasksApi>
